import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { feedingRequestsApi } from '../utils/api';
import { handleApiError, formatDate } from '../utils/helpers';
import PhotoModal from '../components/PhotoModal';

const FeedingHistoryPage = () => {
  const [feedings, setFeedings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(10);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [currentFeedingPhotos, setCurrentFeedingPhotos] = useState([]);
  
  // Fetch processed feeding requests
  useEffect(() => {
    fetchFeedings();
  }, [currentPage, pageSize]);
  
  const fetchFeedings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the new API method for processed feeding requests
      const response = await feedingRequestsApi.getProcessedFeedingRequests(currentPage, pageSize);
      console.log('Processed feeding requests response:', response);
      
      // Handle the case when the API returns an array directly
      const feedingsData = Array.isArray(response) ? response : (response.data || []);
      console.log('Processed feedings data:', feedingsData);
      
      setFeedings(feedingsData);
      setTotalPages(Math.ceil((response.totalCount || feedingsData.length) / pageSize));
    } catch (error) {
      console.error('Error fetching feeding history:', error);
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };
  
  // Open photo modal
  const openPhotoModal = (photos, index) => {
    console.log('openPhotoModal called with photos:', photos, 'index:', index);
    
    // Make sure we have an array of photos
    let photoArray = photos;
    if (!Array.isArray(photos)) {
      if (photos) {
        photoArray = [photos];
      } else {
        photoArray = [];
      }
    }
    
    console.log('Processed photo array:', photoArray);
    
    setCurrentFeedingPhotos(photoArray);
    setCurrentPhotoIndex(index || 0);
    setPhotoModalOpen(true);
  };
  
  // Close photo modal
  const closePhotoModal = () => {
    console.log('Closing photo modal');
    setPhotoModalOpen(false);
    setCurrentFeedingPhotos([]);
    setCurrentPhotoIndex(0);
  };
  
  // Navigate to previous photo
  const prevPhoto = () => {
    if (!currentFeedingPhotos || currentFeedingPhotos.length <= 1) return;
    
    console.log('prevPhoto called, current index:', currentPhotoIndex);
    const newIndex = (currentPhotoIndex - 1 + currentFeedingPhotos.length) % currentFeedingPhotos.length;
    console.log('New index:', newIndex);
    setCurrentPhotoIndex(newIndex);
  };
  
  // Navigate to next photo
  const nextPhoto = () => {
    if (!currentFeedingPhotos || currentFeedingPhotos.length <= 1) return;
    
    console.log('nextPhoto called, current index:', currentPhotoIndex);
    const newIndex = (currentPhotoIndex + 1) % currentFeedingPhotos.length;
    console.log('New index:', newIndex);
    setCurrentPhotoIndex(newIndex);
  };
  
  // Render pagination controls
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="pagination">
        <button
          className="pagination-button"
          onClick={() => handlePageChange(0)}
          disabled={currentPage === 0}
        >
          &laquo;
        </button>
        
        <button
          className="pagination-button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 0}
        >
          &lsaquo;
        </button>
        
        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            className={`pagination-button ${currentPage === i ? 'active' : ''}`}
            onClick={() => handlePageChange(i)}
          >
            {i + 1}
          </button>
        ))}
        
        <button
          className="pagination-button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages - 1}
        >
          &rsaquo;
        </button>
        
        <button
          className="pagination-button"
          onClick={() => handlePageChange(totalPages - 1)}
          disabled={currentPage === totalPages - 1}
        >
          &raquo;
        </button>
      </div>
    );
  };
  
  return (
    <div className="feeding-history-page">
      <div className="page-header">
        <h1 className="page-title">Completed Feeding Requests</h1>
        <p className="page-description">
          View your feeding requests that have been fulfilled by feeders
        </p>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Error state */}
      {error && !loading && (
        <div className="error-message">
          <p>{error}</p>
          <button 
            className="button"
            onClick={fetchFeedings}
          >
            Try Again
          </button>
        </div>
      )}
      
      {/* Empty state */}
      {!loading && !error && (!feedings || feedings.length === 0) && (
        <div className="empty-state">
          <div className="empty-state-icon">🍽️</div>
          <h2 className="empty-state-title">No completed feedings</h2>
          <p className="empty-state-description">
            You don't have any completed feeding requests yet. Check back later!
          </p>
          <Link to="/dashboard/cats" className="button">
            Browse Cats
          </Link>
        </div>
      )}
      
      {/* Feedings list */}
      {!loading && !error && feedings && feedings.length > 0 && (
        <div className="feedings-list">
          {feedings.map((request, index) => {
            console.log(`Rendering processed request ${index}:`, request);
            
            if (!request || !request.cat) {
              console.error(`Invalid request data at index ${index}:`, request);
              return null;
            }
            
            return (
              <div key={request.id || index} className="feeding-item">
                <div className="feeding-cat-info">
                  <img 
                    src={(request.cat.catPhotos && request.cat.catPhotos.length > 0) 
                      ? request.cat.catPhotos[0].path 
                      : '/cat-placeholder.png'} 
                    alt={request.cat.name || 'Cat'} 
                    className="feeding-cat-image"
                    onError={(e) => {
                      console.log('Image failed to load, using fallback');
                      e.target.src = '/cat-placeholder.png';
                    }}
                  />
                  <div className="feeding-details">
                    <h3>{request.cat.name || 'Unknown Cat'}</h3>
                    <p className="feeding-date">
                      Requested on {request.createdAt 
                        ? new Date(request.createdAt).toLocaleDateString() 
                        : new Date().toLocaleDateString()}
                    </p>
                    <p className="feeding-status">
                      Status: <span className="status-completed">Completed</span>
                    </p>
                    {request.feeding && (
                      <p className="feeding-date">
                        Fed on {request.feeding.date 
                          ? new Date(request.feeding.date).toLocaleDateString() 
                          : (request.feeding.feedingTime 
                            ? new Date(request.feeding.feedingTime).toLocaleDateString()
                            : 'Unknown date')}
                      </p>
                    )}
                    
                    {/* Feeding Notes */}
                    {request.feeding && request.feeding.notes && (
                      <p className="feeding-notes">
                        <span className="notes-label">Notes:</span> {request.feeding.notes}
                      </p>
                    )}
                  </div>
                </div>
                <div className="feeding-actions">
                  {request.feeding && request.feeding.feedingPhotos && request.feeding.feedingPhotos.length > 0 && (
                    <button
                      className="button photo-button"
                      onClick={() => {
                        console.log('Opening photo modal with photos:', request.feeding.feedingPhotos);
                        openPhotoModal(request.feeding.feedingPhotos.map(p => p.path), 0);
                      }}
                    >
                      View Photos ({request.feeding.feedingPhotos.length})
                    </button>
                  )}
                  <Link 
                    to={`/dashboard/cats/${request.cat.id}`} 
                    className="button secondary"
                  >
                    View Cat
                  </Link>
                </div>
              </div>
            );
          })}
          
          {/* Pagination */}
          {totalPages > 1 && renderPagination()}
        </div>
      )}
      
      {/* Photo modal */}
      {photoModalOpen && currentFeedingPhotos && currentFeedingPhotos.length > 0 && (
        <PhotoModal
          photos={currentFeedingPhotos}
          currentIndex={currentPhotoIndex}
          onClose={closePhotoModal}
          onPrev={prevPhoto}
          onNext={nextPhoto}
        />
      )}
    </div>
  );
};

export default FeedingHistoryPage; 